import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import {
  VoicesCompanyPage,
  VoicesCompanyPagePayload,
} from "../../../Models/voicesProfiles";
import { useSaveVoicesCompanyPage } from "../../../Query/useVoicesProfiles";
import { ComoButton } from "../../Common/ComoButton";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";
import VoicesPublishingFields from "./VoicesPublishingFields";

interface Props {
  open: boolean;
  page: VoicesCompanyPage | null;
  onClose: () => void;
}

const EMPTY_PAGE: VoicesCompanyPagePayload = {
  name: "",
  preferences: "",
  buffer_channel_id: null,
  weekly_limit: null,
  blocked_terms: [],
};

const VoicesCompanyPageDialog: React.FC<Props> = ({ open, page, onClose }) => {
  const { t } = useTranslation("voicesProfiles");
  const savePage = useSaveVoicesCompanyPage();
  const [form, setForm] = useState<VoicesCompanyPagePayload>(EMPTY_PAGE);

  useEffect(() => {
    if (!open) return;
    setForm(
      page
        ? {
            name: page.name,
            preferences: page.preferences,
            buffer_channel_id: page.buffer_channel_id,
            weekly_limit: page.weekly_limit,
            blocked_terms: page.blocked_terms,
          }
        : EMPTY_PAGE,
    );
  }, [open, page]);

  const handleSave = () => {
    savePage.mutate(
      { pageId: page ? page.id : null, data: form },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: color.gray900, fontWeight: 600 }}>
        {page ? t("editCompanyPage") : t("newCompanyPage")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <VoicesPublishingFields
            value={form}
            channelType="page"
            onChange={(settings, channel) =>
              setForm({
                ...form,
                ...settings,
                name:
                  !form.name.trim() && channel?.display_name
                    ? channel.display_name
                    : form.name,
              })
            }
          />
          <VoicesField label={t("companyPageName")}>
            <VoicesInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </VoicesField>
          <VoicesField label={t("preferences")} hint={t("companyPreferencesHint")}>
            <VoicesInput
              multiline
              minRows={4}
              value={form.preferences}
              onChange={(e) => setForm({ ...form, preferences: e.target.value })}
            />
          </VoicesField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: color.gray600, textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <ComoButton
          onClick={handleSave}
          disabled={!form.name.trim() || savePage.isPending}
        >
          {t("save")}
        </ComoButton>
      </DialogActions>
    </Dialog>
  );
};

export default VoicesCompanyPageDialog;
